/**
 * American Vek: results of the "Frete no produto" app block (snippets/av-shipping-calc).
 * - Masks the CEP as 00000-000.
 * - Sorts the delivery options by price, moves store pickup to the end with a short label
 *   ("Retirar na loja" + street and district) and tags "Mais barato" / "Mais rápido" (both on one
 *   option when it is cheapest and fastest) when there are two or more delivery options.
 * The app replaces #calc-carrier's content on every calculation, so a MutationObserver re-applies
 * this to each new list (marked with data-av-done to skip our own changes).
 */
const parsePrice = (text) => {
  if (/gr[áa]tis/i.test(text)) return 0;
  const value = parseFloat(text.replace(/[^\d,]/g, '').replace(',', '.'));
  return Number.isNaN(value) ? Infinity : value;
};

const parseDays = (text) => {
  if (/hoje/i.test(text)) return 0;
  const match = text.match(/\d+/);
  return match ? Number(match[0]) : Infinity;
};

const tag = (label, modifier) => {
  const el = document.createElement('span');
  el.className = `av-ship__tag av-ship__tag--${modifier}`;
  el.textContent = label;
  return el;
};

function enhance(list) {
  list.dataset.avDone = '';
  const rows = [...list.children].filter((el) => el.tagName === 'LI');
  const options = rows.map((li) => {
    const [name, eta] = li.querySelectorAll('.ship-name > span');
    const price = li.querySelector('.ship-price');
    return {
      li,
      name,
      pickup: /^retir/i.test(name?.textContent.trim() ?? ''),
      price: parsePrice(price?.textContent ?? ''),
      days: parseDays(eta?.textContent ?? ''),
    };
  });

  for (const option of options) {
    if (option.price === 0) option.li.classList.add('av-ship--free');
    if (!option.pickup || !option.name) continue;
    option.li.classList.add('av-ship--pickup');
    // "Retirar na loja - Rua X, 162 - Bairro - CEP: … - Cidade" -> "Retirar na loja" + "Rua X, 162 – Bairro · Hoje"
    const parts = option.name.textContent.split(' - ').map((part) => part.trim());
    const eta = option.name.nextElementSibling;
    if (parts.length > 1 && eta) {
      option.name.title = option.name.textContent;
      option.name.textContent = parts[0];
      eta.textContent = `${parts.slice(1, 3).join(' – ')} · ${eta.textContent.trim()}`;
    }
  }

  const delivery = options.filter((option) => !option.pickup);
  delivery.sort((a, b) => a.price - b.price || a.days - b.days);
  for (const option of [...delivery, ...options.filter((option) => option.pickup)]) list.append(option.li);

  if (delivery.length < 2) return;
  const cheapest = delivery[0];
  const fastest = delivery.reduce((best, option) =>
    option.days < best.days || (option.days === best.days && option.price < best.price) ? option : best
  );
  for (const option of delivery) {
    const tags = [];
    if (option === cheapest) tags.push(tag('Mais barato', 'cheap'));
    if (option === fastest) tags.push(tag('Mais rápido', 'fast'));
    if (!tags.length) continue;
    const wrap = document.createElement('span');
    wrap.className = 'av-ship__tags';
    wrap.append(...tags);
    // Beside the delivery time ("5 dias úteis  Mais barato").
    (option.li.querySelector('.ship-name > span:nth-child(2)') ?? option.li.querySelector('.ship-name'))?.append(wrap);
  }
}

function maskCep(event) {
  const input = /** @type {HTMLInputElement} */ (event.target);
  if (!(input instanceof HTMLInputElement) || input.id !== 'zipcode') return;
  const digits = input.value.replace(/\D/g, '').slice(0, 8);
  const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  if (masked !== input.value) input.value = masked;
}

function init() {
  const carrier = document.getElementById('calc-carrier');
  const form = document.getElementById('formKokfyShipping');
  if (!carrier || carrier.dataset.avInit !== undefined) return Boolean(carrier);
  carrier.dataset.avInit = '';
  form?.addEventListener('input', maskCep);
  const apply = () => {
    const list = carrier.querySelector('ul');
    if (list && list.dataset.avDone === undefined && list.querySelector('li')) enhance(list);
  };
  new MutationObserver(apply).observe(carrier, { childList: true, subtree: true });
  apply();
  return true;
}

// The app builds its markup after load; wait for it.
if (!init()) {
  const observer = new MutationObserver(() => init() && observer.disconnect());
  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 20000);
}
